'use client';

import * as React from 'react';
import Script from 'next/script';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';

interface CalendlyButtonProps {
  text?: string;
  className?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost' | 'link' | 'destructive';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export function CalendlyButton({ 
  text = 'Book a call', 
  className,
  variant = 'default',
  size = 'default'
}: CalendlyButtonProps): React.JSX.Element {
  const [isClient, setIsClient] = React.useState(false);

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  const openCalendly = React.useCallback(() => {
    if (!isClient) return;
    
    const calendlyWindow = window as Window & { Calendly?: { initPopupWidget: (options: { url: string }) => void } };
    if (calendlyWindow.Calendly) {
      calendlyWindow.Calendly.initPopupWidget({
        url: 'https://calendly.com/abubakarr_jaye-alumni/30min'
      });
    } else {
      // Fallback: open Calendly in new tab
      window.open('https://calendly.com/abubakarr_jaye-alumni/30min', '_blank', 'noopener,noreferrer');
    }
  }, [isClient]);

  return (
    <Button
      variant={variant}
      size={size}
      onClick={openCalendly}
      className={cn('calendly-button', className)}
      type="button"
    >
      {text}
    </Button>
  );
}

// Separate component to handle script loading
export function CalendlyScriptLoader(): React.JSX.Element | null {
  const [shouldLoad, setShouldLoad] = React.useState(false);

  React.useEffect(() => {
    setShouldLoad(true);
  }, []);

  if (!shouldLoad) return null;

  return (
    <>
      <Script
        id="calendly-widget"
        src="https://assets.calendly.com/assets/external/widget.js"
        strategy="afterInteractive"
      />
      <link
        href="https://assets.calendly.com/assets/external/widget.css"
        rel="stylesheet"
      />
    </>
  );
}